import { Card, CardContent } from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { Users, Database, Info, Phone, Video, Calendar } from "lucide-react";
import { UsageData } from "@/features/billing/contexts/SubscriptionContext";
import { useState, useEffect } from "react";
import { apiClient } from "@/shared/services/apiClient";
import { API_ROUTES } from "@/shared/services/apiRoutes";
import { useToast } from "@/shared/hooks/use-toast";
import { useSubscription } from "@/features/billing/contexts/SubscriptionContext";
import { supabase } from "@/integrations/supabase/client";

interface CurrentPlanCardProps {
  usage: UsageData;
  onManageSubscription: () => void;
  onSubscribe?: () => void;
  loading: boolean;
  processingPayment?: boolean;
  footageTooltip: string;
}

export function CurrentPlanCard({ usage, onManageSubscription, onSubscribe, loading, processingPayment = false, footageTooltip }: CurrentPlanCardProps) {
  const [resuming, setResuming] = useState(false);
  const [hasReferralCode, setHasReferralCode] = useState(false);
  const { toast } = useToast();
  const { refreshSubscription } = useSubscription();

  // Check if user has referral code
  useEffect(() => {
    const checkReferralCode = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      const referralCode = user?.user_metadata?.referral_code || localStorage.getItem('pendingReferralCode');
      setHasReferralCode(!!referralCode);
    };
    checkReferralCode();
  }, []);

  // DEBUG: Log usage data to console
  console.log('[CurrentPlanCard] Received usage data:', {
    subscribed: usage?.subscribed,
    isTrialActive: usage?.isTrialActive,
    subscription_status: usage?.subscription_status,
    subscriptionStatus: usage?.subscriptionStatus,
    subscriptionTier: usage?.subscriptionTier,
    planType: usage?.planType,
    fullUsage: usage
  });

  const handleUnpause = async () => {
    try {
      setResuming(true);

      const response = await apiClient.post(API_ROUTES.BILLING_RESUME);

      if (response.ok) {
        toast({
          title: 'Subscription Resumed',
          description: 'Your subscription has been reactivated successfully.',
          variant: 'default',
        });

        // Refresh subscription data without reloading page
        await refreshSubscription();
      } else {
        throw new Error(response.error || 'Failed to resume subscription');
      }
    } catch (error: any) {
      console.error('[CurrentPlanCard] Resume error:', error);
      toast({
        title: 'Resume Failed',
        description: error.message || 'Failed to resume subscription. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setResuming(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">Current Plan</h2>
      <Card className="h-full">
        <CardContent className="p-6 space-y-6">
          {/* Plan Header with Status Badge - Matching Mobile */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-lg">
                  {usage?.subscribed
                    ? (usage.subscriptionTier ||
                       (usage.maxUsers > 1 ? 'wlkthru Team' : 'wlkthru Pro'))
                    : usage?.isTrialActive ? 'Trial' : 'No Active Plan'}
                </p>
                {/* Status Badge - matches mobile logic exactly */}
                {usage?.subscription_status === 'canceled' ? (
                  // Canceled subscription - red badge (highest priority)
                  <Badge variant="secondary" className="bg-red-100 text-red-700">
                    Canceled
                  </Badge>
                ) : usage?.is_paused ? (
                  // Paused subscription - yellow badge
                  <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">
                    Paused
                  </Badge>
                ) : usage?.subscribed && usage?.isTrialActive ? (
                  // Paid Trial (Stripe) - card on file, green badge
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    Pro Trial
                  </Badge>
                ) : usage?.subscribed ? (
                  // Active paid subscription
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    Active
                  </Badge>
                ) : usage?.isTrialActive ? (
                  // Free Trial (App) - no card, blue badge
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                    Trial
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="bg-gray-100 text-gray-700">
                    Inactive
                  </Badge>
                )}
              </div>

              {/* Trial Info Panel - matching mobile's distinction (hide for referred users) */}
              {usage?.isTrialActive && !hasReferralCode && (
                <div className="mt-3 p-3 rounded-lg border">
                  {/* Trail (app) - no card */}
                  {!usage.subscribed && usage.trialType === 'app' && (
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-yellow-700">Trial Active</p>
                      <p className="text-xs text-muted-foreground">
                        {usage.daysLeftInTrial || 15} days left • No payment required
                      </p>
                    </div>
                  )}

                  {/* Paid Trial (stripe) - card on file */}
                  {usage.subscribed && usage.trialType === 'stripe' && (
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-green-700">Trial Period</p>
                      <p className="text-xs text-muted-foreground">
                        {usage.daysLeftInTrial || 15} days until first payment • Card on file
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Referral Discount Info Panel - show for referred users */}
              {hasReferralCode && !usage?.subscribed && (
                <div className="mt-3 p-3 rounded-lg border border-green-200 bg-green-50">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-green-700">🎉 Special Referral Offer</p>
                    <p className="text-xs text-green-600">
                      Save $100 on your first 3 months • Pay only $66/month
                    </p>
                  </div>
                </div>
              )}

              {/* Billing cycle (only show for paid subscriptions not in trial) */}
              {usage?.subscribed && !usage?.isTrialActive && usage.billingCycle && (
                <p className="text-sm text-muted-foreground mt-1">
                  Billed {usage.billingCycle}
                </p>
              )}

              {/* Renewal date (only show for active paid subscriptions not in trial) */}
              {usage?.subscribed && !usage.isTrialActive && !usage.is_paused && usage.nextBillingDate && (
                <p className="text-sm text-muted-foreground">
                  Renews on {new Date(usage.nextBillingDate).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>

          {/* Usage Stats Grid - Only show if user has active plan */}
          {(usage?.subscribed || usage?.isTrialActive) && (
            <div className="grid grid-cols-1 gap-4">
              {/* Users */}
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <Users className="h-5 w-5 text-primary mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Team Members</p>
                  <p className="text-sm text-muted-foreground">
                    {usage?.currentUsers || 0} / {usage?.maxUsers || 1} users
                  </p>
                </div>
              </div>

              {/* Storage */}
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <Database className="h-5 w-5 text-primary mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">Storage Retention</p>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-muted-foreground cursor-help flex-shrink-0" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="text-sm">{footageTooltip}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <p className="text-sm text-muted-foreground break-words">
                    {usage?.retentionDays || 30} days
                  </p>
                </div>
              </div>

              {/* Calls Usage - Only show if limit exists (free trial) */}
              {usage?.calls_limit !== null && usage?.calls_limit !== undefined && (
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <Phone className="h-5 w-5 text-primary mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Calls</p>
                    <p className="text-sm text-muted-foreground">
                      {usage?.calls_count || 0} / {usage?.calls_limit} used
                    </p>
                  </div>
                </div>
              )}

              {/* Recordings Usage - Only show if limit exists (free trial) */}
              {usage?.recordings_limit !== null && usage?.recordings_limit !== undefined && (
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <Video className="h-5 w-5 text-primary mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Recordings</p>
                    <p className="text-sm text-muted-foreground">
                      {usage?.recordings_count || 0} / {usage?.recordings_limit} used
                    </p>
                  </div>
                </div>
              )}

              {/* Next Billing Date - Only show for paid users */}
              {usage?.next_billing_date && (usage?.subscribed || usage?.is_pro_trial) && (
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <Calendar className="h-5 w-5 text-primary mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Next Billing</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(usage.next_billing_date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              )}

              {/* Pro Trial End Date - Only show for pro trial users */}
              {usage?.is_pro_trial && usage?.pro_trial_end_date && (
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <Calendar className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900">Pro Trial Ends</p>
                    <p className="text-sm text-blue-700">
                      {new Date(usage.pro_trial_end_date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          {usage?.is_paused ? (
            /* Paused subscription - show Unpause button */
            <Button
              onClick={handleUnpause}
              disabled={resuming || loading}
              className="w-full"
            >
              {resuming ? 'Resuming...' : 'Unpause Subscription'}
            </Button>
          ) : usage?.subscribed && usage?.subscription_status === 'canceled' ? (
            /* Subscription canceled but still has access until period end - show message and reactivate button */
            <div className="space-y-3">
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm font-medium text-orange-900">Subscription Ending Soon</p>
                <p className="text-xs text-orange-700 mt-1">
                  Access ends on{' '}
                  {usage?.nextBillingDate ? new Date(usage.nextBillingDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : usage?.billingCycleEnd ? new Date(usage.billingCycleEnd).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'your billing period end'}
                </p>
              </div>
              <Button
                onClick={onSubscribe || (() => window.location.href = '/')}
                disabled={loading || processingPayment}
                className="w-full"
              >
                Reactivate Subscription
              </Button>
            </div>
          ) : usage?.subscribed && usage?.subscription_status !== 'canceled' && !usage?.cancellationDate ? (
            /* Active subscription - show Manage button */
            <Button
              onClick={onManageSubscription}
              disabled={loading}
              className="w-full"
              variant="outline"
            >
              {loading ? 'Loading...' : 'Manage Subscription'}
            </Button>
          ) : (
            /* No active subscription (canceled or never subscribed) - show Subscribe button */
            <Button
              onClick={onSubscribe || (() => window.location.href = '/')}
              disabled={loading || processingPayment}
              className={hasReferralCode ? "w-full bg-green-600 hover:bg-green-700" : "w-full"}
            >
              {hasReferralCode
                ? 'Subscribe & Save $100'
                : usage?.canStartTrial
                  ? 'Start your free trial'
                  : 'Subscribe Now'}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
